import { useCallback, useMemo, useState, type ReactNode } from "react";
import type { MyPayEnvironment } from "../../generated/core-api";
import { useComponentMatchingImportInvocation } from "../hooks/useComponentMatchingImportInvocation";
import type {
  BoundPayRun,
  MatchingParseError,
  MatchingRow,
} from "../types/matching";
import {
  MatchingWizardCtx,
  type MatchingWizardContextValue,
} from "./MatchingWizardContext";

export interface MatchingWizardProviderProps {
  companyCode: string;
  companyName: string;
  mypayEnvironment: MyPayEnvironment;
  boundPayRun: BoundPayRun | null;
  children: ReactNode;
}

export function MatchingWizardProvider({
  companyCode,
  companyName,
  mypayEnvironment,
  boundPayRun,
  children,
}: MatchingWizardProviderProps) {
  const manager = useComponentMatchingImportInvocation();
  const [fileName, setFileName] = useState<string | null>(null);
  const [rawCsvText, setRawCsvText] = useState<string | null>(null);
  const [parsedRows, setParsedRows] = useState<MatchingRow[] | null>(null);
  const [parseErrors, setParseErrors] = useState<MatchingParseError[]>([]);
  const [failedUploadAttempt, setFailedUploadAttempt] = useState<{
    fileName: string;
    rawCsvText: string | null;
    parseErrors: MatchingParseError[];
  } | null>(null);

  const setUploadSuccess = useCallback(
    (nextFileName: string, nextRaw: string, rows: MatchingRow[]) => {
      setFileName(nextFileName);
      setRawCsvText(nextRaw);
      setParsedRows(rows);
      setParseErrors([]);
      setFailedUploadAttempt(null);
      // Pay run is bound in the UI before upload; stamp is already on each row.
      const payRunId = boundPayRun?.payRunId ?? rows[0]?.payRunId;
      if (payRunId) {
        manager.setPendingRows(
          rows,
          companyCode,
          payRunId,
          mypayEnvironment,
          nextFileName
        );
      }
    },
    [boundPayRun?.payRunId, companyCode, manager, mypayEnvironment]
  );

  const setUploadParseFailure = useCallback(
    (
      nextFileName: string,
      nextRaw: string,
      rows: MatchingRow[],
      errors: MatchingParseError[]
    ) => {
      // Drop any prior successful batch so a bad replacement cannot leave the
      // old validate/commit report mounted under the new parse errors.
      manager.clear();
      setFileName(nextFileName);
      setRawCsvText(nextRaw);
      setParsedRows(rows);
      setParseErrors(errors);
      setFailedUploadAttempt(null);

      // Parsable rows stay importable (partial import) — queue them for
      // validate/commit while stubs remain on `parsedRows` for the report.
      const badFileRows = new Set(
        errors
          .map((e) => e.fileRow)
          .filter((r): r is number => r !== null)
      );
      const goodRows = rows.filter((r) => !badFileRows.has(r.fileRow));
      const payRunId = boundPayRun?.payRunId ?? goodRows[0]?.payRunId;
      if (goodRows.length > 0 && payRunId) {
        manager.setPendingRows(
          goodRows,
          companyCode,
          payRunId,
          mypayEnvironment,
          nextFileName
        );
      }
    },
    [boundPayRun?.payRunId, companyCode, manager, mypayEnvironment]
  );

  const updateParsedRows = useCallback(
    (rows: MatchingRow[]) => {
      setParsedRows(rows);
      manager.updateRowsLocally(rows);
    },
    [manager]
  );

  const clearUpload = useCallback(() => {
    setFileName(null);
    setRawCsvText(null);
    setParsedRows(null);
    setParseErrors([]);
    setFailedUploadAttempt(null);
    manager.clear();
  }, [manager]);

  const value = useMemo<MatchingWizardContextValue>(
    () => ({
      companyCode,
      companyName,
      mypayEnvironment,
      boundPayRun,
      fileName,
      rawCsvText,
      parsedRows,
      parseErrors,
      failedUploadAttempt,
      manager,
      setUploadSuccess,
      setUploadParseFailure,
      updateParsedRows,
      setFailedUploadAttempt,
      clearUpload,
    }),
    [
      companyCode,
      companyName,
      mypayEnvironment,
      boundPayRun,
      fileName,
      rawCsvText,
      parsedRows,
      parseErrors,
      failedUploadAttempt,
      manager,
      setUploadSuccess,
      setUploadParseFailure,
      updateParsedRows,
      clearUpload,
    ]
  );

  return (
    <MatchingWizardCtx.Provider value={value}>
      {children}
    </MatchingWizardCtx.Provider>
  );
}
